<template>
  <div ref="app">
    <filter-bar 
      v-if="facets.length > 1"
      :facets="facets" 
      v-model:state="query"
      :sortOptions="sortOptions" 
      :perPageOptions="perPageOptions" />

    <category-filter 
      v-if="facets.length === 1" 
      :categories="facets[0].options" 
      :selected="query.filters[facets[0].name] ? query.filters[facets[0].name][0] : ''" 
      @change="val => onCategoryChange(facets[0].name, val)" />

    <spinner v-if="isLoading" />

    <slot 
      v-if="!isLoading && !data.error" 
      :data="data"/>

    <alert v-if="!isLoading && data.error">
      Sorry, there was a problem retrieving results. Please try again later.
    </alert>

    <pagination
      v-if="data.pagination"
      :total-pages="data.pagination.totalPages"
      :total="data.pagination.totalResults"
      :per-page="Number(query.perPage) || defaultPerPage"
      :page="query.page"
      :path="queryString"
      @page-changed="onPageChange"
    />
  </div>
</template>

<script>
import {cloneDeep, isArrayLike, isEmpty, isEqual, isNil, omitBy} from 'lodash';
import Alert from '../UI/Alert.vue';
import Pagination from '../UI/Pagination.vue';
import Spinner from '../UI/Spinner.vue';
import FilterBar from './FilterBar.vue';
import CategoryFilter from './CategoryFilter.vue';
import { fetcher } from '../../../../utils/fetcher';
import getStateFromUrl from './get-state-from-url';

const [path] = window.location.href.split('?');

export default {
  components: {
    Pagination,
    Spinner,
    FilterBar,
    CategoryFilter,
    Alert
  },

  data() {
    const state = getStateFromUrl(this.facets.map(f => f.name));

    return {
      isInitialised: false,
      isLoading: false,
      hasUserPoppedState: false,
      data: {},
      query: { 
        ...this.getDefaultQuery(),
        ...state
      }
    };
  },

  created() {
    this.getResults(false);    
  },

  mounted() {
    window.addEventListener('popstate', event => {
      this.hasUserPoppedState = true;
      this.query = event.state || this.getDefaultQuery();
    });
  },

  computed: {
    computedQuery() {
      return cloneDeep(this.query);
    },

    queryParams() {
      const { filters, ...other } = this.query;
      const params = { ...cloneDeep(filters), ...other };
      const defaultQuery = this.getDefaultQuery();

      Object.keys(params).forEach(key => {
        if (
          // Filter out null / undefined items
          !params[key] 
          // Filter out empty arrays
          || (Array.isArray(params[key]) && !params[key].filter(item => item).length)
          // Filter out default params
          || isEqual(defaultQuery[key], params[key]) 
        ) {
          delete params[key];
        }
      });
      
      return params;
    },

    /**
     * Gets the body to be POST-ed to the API
     * @returns The body of the request sent to the API
     */
    postParams() {
      // Flatten out the filters object, and add in all the default query parameters for anything that hasn't
      // been explicitly set by the user
      const { filters, ...other } = this.query;
      const { filters: defaultFilters, ...defaultOther } = this.getDefaultQuery();
      const fullQuery = { ...defaultFilters, ...defaultOther, ...filters, ...other };
      
      // Remove null and empty values to keep things tidy
      return omitBy(fullQuery, x => isNil(x) || (isArrayLike(x) && isEmpty(x)));
    },

    queryString() {
      return new URLSearchParams(this.queryParams).toString();
    }
  },

  watch: {
    // Watch computedQuery instead of query as `newValue` 
    // and `oldValue` are equal on nested mutations 
    computedQuery: {
      handler(newQuery, oldQuery){
        // Reset page number if query changed
        if( 
          (!isEqual(newQuery.filters, oldQuery.filters)
          || oldQuery.perPage !== newQuery.perPage 
          || oldQuery.sortBy !== newQuery.sortBy)
          && newQuery.page !== 1
        ) {
          this.query.page = 1;
          return;
        }

        if(!this.hasUserPoppedState) {
          this.updateQueryString();
        } else {
          this.hasUserPoppedState = false;
        }

        this.getResults();
      },
      deep: true
    }
  },

  methods: {
    updateQueryString() {
      window.history.pushState(
        cloneDeep(this.query), 
        '', 
        this.queryString ? `${path}?${this.queryString}` : path
      );
    },

    getDefaultQuery() {
      const filters = {};

      for (let index = 0; index < this.facets.length; index++) {
        filters[this.facets[index].name] = [];
      }

      return {
        filters,
        sortBy: this.defaultSort,
        perPage: this.defaultPerPage.toString(),
        page: 1,
      };
    },

    async getResults(scrollIntoView = true) {
      if( this.controller ) {
        this.controller.abort();
      }

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.isLoading = true;
     
      this.data = await fetcher.post(this.endpoint, this.postParams, {
        signal
      });

      this.isLoading = false;

      if( scrollIntoView ) {
        this.$refs.app.scrollIntoView({ 
          block: "start", 
          inline: "nearest", 
          behavior: "smooth"
        });
      }
    },

    onPageChange(page) {
      this.query.page = page;
    },

    onCategoryChange(key, category) {
      this.query.filters[key] = category == null ? [] : [category];
    }
  },

  props: {
    endpoint: {
      type: String,
      default: null
    },
    facets: {
      type: Array,
      default: () => []
    },
    sortOptions: Array,
    perPageOptions: Array,
    defaultSort: {
      type: String,
      default: ''
    },
    defaultPerPage: {
      type: Number,
      default: 50
    }
  }
}
</script>